import React, { Component } from 'react';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { parseDateFromISO8601, stringifyDateToISO8601 } from '../../../util/dates';
import {
  MARKETPLACE_TIME_ZONE,
  parseDateContext,
  DATE_MODE_QUERY_PARAM,
} from '../../../util/searchDates';

import { FieldDateRangeController } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import FilterPopupForSidebar from './FilterPopupForSidebar';
import DayOrMonthPicker from './DayOrMonthPicker';
import css from './BookingDateRangeFilter.module.css';

const getDatesQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'dates';
};

// Parse query parameter, which should look like "2020-05-28,2020-05-31"
const parseValue = value => {
  const rawValuesFromParams = value ? value.split(',') : [];
  const [startDate, endDate] = rawValuesFromParams.map(v => parseDateFromISO8601(v));
  return value && startDate && endDate ? { dates: { startDate, endDate } } : { dates: null };
};
// Format dateRange value for the query. It's given by FieldDateRangeInput:
// { dates: { startDate, endDate } }
const formatValue = (dateRange, queryParamName) => {
  const hasDates = dateRange && dateRange.dates;
  const { startDate, endDate } = hasDates ? dateRange.dates : {};
  const start = startDate ? stringifyDateToISO8601(startDate) : null;
  const end = endDate ? stringifyDateToISO8601(endDate) : null;
  const value = start && end ? `${start},${end}` : null;
  // The stock check-in/check-out controller only ever means "the whole span must be free", even
  // when the span happens to be a whole calendar month. Clearing dateMode says so — without it,
  // a month left in the URL by the day/month picker (or by a link) would still be qualifying a
  // range the user has just replaced.
  return { [queryParamName]: value, [DATE_MODE_QUERY_PARAM]: null };
};

/**
 * BookingDateRangeFilter component
 *
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.id - The ID
 * @param {React.Node} [props.label] - The label
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {boolean} [props.liveEdit] - Whether to live edit
 * @param {Array<string>} [props.queryParamNames] - The query param names (e.g. ['dates'])
 * @param {Function} props.onSubmit - The function to submit
 * @param {number} [props.minimumNights] - The minimum nights (default: 0)
 * @param {Object} [props.initialValues] - The initial values
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
export class BookingDateRangeFilterComponent extends Component {
  constructor(props) {
    super(props);

    this.state = { isOpen: true };

    this.popupControllerRef = null;
    this.plainControllerRef = null;

    this.toggleIsOpen = this.toggleIsOpen.bind(this);
  }

  toggleIsOpen() {
    this.setState(prevState => ({ isOpen: !prevState.isOpen }));
  }

  render() {
    const {
      className,
      rootClassName,
      showAsPopup = true,
      isDesktop = false,
      initialValues,
      id,
      contentPlacementOffset = 0,
      onSubmit,
      queryParamNames,
      label,
      intl,
      minimumNights = 0,
      getAriaLabel = () => {},
      // Feature gate (configSearch.dateRangeFilter.dateSearch). Off, this component renders
      // exactly what it always has.
      dateSearch = false,
      ...rest
    } = this.props;

    const datesQueryParamName = getDatesQueryParamName(queryParamNames);
    const initialDates =
      initialValues && initialValues[datesQueryParamName]
        ? parseValue(initialValues[datesQueryParamName])
        : { dates: null };

    const isSelected = !!initialDates.dates;
    const startDate = isSelected ? initialDates.dates.startDate : null;
    const endDate = isSelected ? initialDates.dates.endDate : null;

    const format = {
      month: 'short',
      day: 'numeric',
    };

    const formattedStartDate = isSelected ? intl.formatDate(startDate, format) : null;
    const formattedEndDate = isSelected ? intl.formatDate(endDate, format) : null;

    // A wedding is one day, so with the gate on the header has to say so: '14. Juni 2027' for a
    // day and 'Juni 2027' for a month, never '14. Juni - 14. Juni'. The context is read from the
    // same `dates` param the picker writes, and formatted in the marketplace's zone because the
    // Dates it parses are Zurich midnights.
    const dateContext = dateSearch
      ? parseDateContext(
          initialValues?.[datesQueryParamName],
          initialValues?.[DATE_MODE_QUERY_PARAM]
        )
      : null;

    // The picker cannot emit a 'range' context, but a link, a bookmark or a hand-edited URL can
    // still carry one into a gate-on page. Label it for what it is — the whole span — rather
    // than letting it fall through to the month branch and read as a month it is not.
    const formatDay = isoDate =>
      intl.formatDate(parseDateFromISO8601(isoDate, MARKETPLACE_TIME_ZONE), {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: MARKETPLACE_TIME_ZONE,
      });

    const formattedDateContext = !dateContext
      ? null
      : dateContext.mode === 'day'
      ? formatDay(dateContext.date)
      : dateContext.mode === 'range'
      ? `${formatDay(dateContext.start)} - ${formatDay(dateContext.end)}`
      : intl.formatDate(parseDateFromISO8601(`${dateContext.month}-01`, MARKETPLACE_TIME_ZONE), {
          month: 'long',
          year: 'numeric',
          timeZone: MARKETPLACE_TIME_ZONE,
        });

    // Gate off, this is the stock `${start} - ${end}` string and showSelection is exactly
    // isSelected, so every label below renders byte-for-byte what it always did.
    const selectedDatesLabel = dateSearch
      ? formattedDateContext
      : `${formattedStartDate} - ${formattedEndDate}`;
    const showSelection = isSelected && !!selectedDatesLabel;

    const labelForPlain = showSelection
      ? intl.formatMessage(
          { id: 'BookingDateRangeFilter.labelSelectedPlain' },
          {
            dates: selectedDatesLabel,
          }
        )
      : label
      ? label
      : intl.formatMessage({ id: 'BookingDateRangeFilter.labelPlain' });

    const labelForPopup = showSelection
      ? intl.formatMessage(
          { id: 'BookingDateRangeFilter.labelSelectedPopup' },
          {
            dates: selectedDatesLabel,
          }
        )
      : label
      ? label
      : intl.formatMessage({ id: 'BookingDateRangeFilter.labelPopup' });

    const labelSelection = showSelection
      ? intl.formatMessage(
          { id: 'BookingDateRangeFilter.labelSelectedPopup' },
          {
            dates: selectedDatesLabel,
          }
        )
      : null;

    const handleSubmit = values => {
      onSubmit(formatValue(values, datesQueryParamName));
    };

    const onClearPopupMaybe =
      this.popupControllerRef && this.popupControllerRef.onReset
        ? { onClear: () => this.popupControllerRef.onReset(null, null) }
        : {};

    const onCancelPopupMaybe =
      this.popupControllerRef && this.popupControllerRef.onReset
        ? { onCancel: () => this.popupControllerRef.onReset(startDate, endDate) }
        : {};

    const onClearPlainMaybe =
      this.plainControllerRef && this.plainControllerRef.onReset
        ? { onClear: () => this.plainControllerRef.onReset(null, null) }
        : {};

    // The day/month picker is not a final-form field, so it reports straight to onSubmit with
    // already-serialised params instead of going through formatValue. It always hands back both
    // halves — `dateMode` null for a day and for a clear — so the pair can never go stale
    // against each other.
    const dayOrMonthPicker = (
      <DayOrMonthPicker
        value={initialValues?.[datesQueryParamName] || null}
        dateMode={initialValues?.[DATE_MODE_QUERY_PARAM] || null}
        onChange={({ dates, [DATE_MODE_QUERY_PARAM]: dateMode }) =>
          onSubmit({ [datesQueryParamName]: dates, [DATE_MODE_QUERY_PARAM]: dateMode })
        }
      />
    );

    // The picker commits straight to the URL the moment a day or a month is clicked, so the
    // popups' "Abbrechen / Übernehmen" footer would be lying: Cancel would re-submit a value that
    // is already committed and Apply would only round-trip it. Both popup wrappers therefore run
    // in liveEdit mode when the gate is on, which is what FilterPlain already does for the same
    // reason.
    //
    // This is passed AFTER {...rest} on purpose, and passed even when false. The desktop sidebar
    // call site (SearchPageWithGrid) already sets liveEdit on every filter, and the wrappers used
    // to ignore it; now that they honour it, letting rest.liveEdit through would silently strip
    // the stock filter's footer. Deciding it here keeps the gate the only thing that moves.

    // Only the children of the three wrappers below change with the gate — every wrapper and
    // all of its props stay as they are, so the gate-off render is the stock one.
    const popupContent = dateSearch ? (
      dayOrMonthPicker
    ) : (
      <FieldDateRangeController
        name="dates"
        minimumNights={minimumNights}
        controllerRef={node => {
          this.popupControllerRef = node;
        }}
      />
    );

    const plainContent = dateSearch ? (
      dayOrMonthPicker
    ) : (
      <FieldDateRangeController
        name="dates"
        minimumNights={minimumNights}
        controllerRef={node => {
          this.plainControllerRef = node;
        }}
        hasFocusOnMount={false}
      />
    );

    return showAsPopup ? (
      <FilterPopup
        className={className}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        label={labelForPopup}
        isSelected={isSelected}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        {...onClearPopupMaybe}
        {...onCancelPopupMaybe}
        initialValues={initialDates}
        ariaLabel={getAriaLabel(label, labelForPopup)}
        {...rest}
        liveEdit={dateSearch}
      >
        {popupContent}
      </FilterPopup>
    ) : isDesktop ? (
      <FilterPopupForSidebar
        className={className}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        label={label}
        labelSelection={labelSelection}
        isSelected={isSelected}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        {...onClearPopupMaybe}
        {...onCancelPopupMaybe}
        ariaLabel={getAriaLabel(label, labelForPopup)}
        initialValues={initialDates}
        {...rest}
        liveEdit={dateSearch}
      >
        {popupContent}
      </FilterPopupForSidebar>
    ) : (
      <FilterPlain
        className={className}
        rootClassName={rootClassName}
        label={label}
        labelSelection={labelSelection}
        labelSelectionSeparator=":"
        isSelected={isSelected}
        id={`${id}.plain`}
        liveEdit
        onSubmit={handleSubmit}
        {...onClearPlainMaybe}
        initialValues={initialDates}
        ariaLabel={getAriaLabel(label, labelForPopup)}
        {...rest}
      >
        {plainContent}
      </FilterPlain>
    );
  }
}

const BookingDateRangeFilter = injectIntl(BookingDateRangeFilterComponent);

export default BookingDateRangeFilter;
